/**
 * Alcance de rutas del workspace contable/fiscal del Modo Asesor (programa PR-AD).
 *
 * El Layout Dispatcher (AD-2a, `src/pages/Layout.jsx`) envuelve estas rutas con
 * `<AdvisorShell>` cuando el asesor está en god_mode Y el flag `ADVISOR_MODE`
 * está activo. En cualquier otra ruta (o sin flag/god_mode) se renderiza el
 * chrome clásico intacto.
 *
 * ⚠️ MANTENIMIENTO: registrar aquí TODA página contable/fiscal nueva que deba
 * formar parte del workspace del asesor (si el sub-sidebar Focused de AD-2b la
 * lista pero no está en este set, al clicarla se perdería el chrome de asesor).
 *
 * Se matchea por **prefijo de PATHNAME**, no por `currentPageName`: el helper
 * `_getCurrentPage` resuelve por el ÚLTIMO segmento de la URL, así que
 * `/accounting/journal` daría 'Journal' (o 'Home' si no está en PAGE_NAMES) y no
 * detectaría las sub-rutas contables. El prefijo de ruta es robusto para rutas
 * anidadas (`/accounting/*`, `/impuestos/:model/...`).
 */

import { isAdvisorModeEnabled } from '@/lib/featureFlags';

/** Prefijos de ruta dentro del workspace del asesor. */
export const ADVISOR_SCOPED_PREFIXES = [
  '/accounting',
  '/facturas',
  '/facturacion',
  '/factura-detalle',
  '/factura-form',
  '/plantillas-factura',
  '/gastos',
  '/gastos-escaneos',
  '/dietas-gastos',
  '/impuestos',
  '/bancos',
  '/banco-cuenta',
  '/conciliacion',
  '/finanzas',
  '/asistente-agentic', // ARIA — lo lista el sub-sidebar de AD-2b.
];

/**
 * ¿La ruta pertenece al workspace contable/fiscal del asesor?
 * @param {string} pathname  `location.pathname` (sin querystring)
 * @returns {boolean}
 */
export function isAdvisorScopedPath(pathname) {
  if (!pathname) return false;
  // Normaliza la barra final (salvo raíz) para que `/gastos/` matchee igual.
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return ADVISOR_SCOPED_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
}

/**
 * Decisión PURA del Layout Dispatcher (AD-2a): ¿renderizar el `AdvisorShell` en
 * lugar del chrome clásico? Las tres condiciones son AND:
 *   - flag `ADVISOR_MODE` activo (vía `isAdvisorModeEnabled` — ⚠️ gate de
 *     seguridad: sin él el chrome de asesor saldría en PROD),
 *   - el asesor está en god_mode,
 *   - la ruta pertenece al workspace contable/fiscal.
 * Aislado aquí para poder testear la lógica sin montar el `Layout` completo.
 * @param {{ search?: string, inGodMode?: boolean, pathname?: string }} ctx
 * @returns {boolean}
 */
export function shouldRenderAdvisorShell({ search = '', inGodMode = false, pathname = '' }) {
  return isAdvisorModeEnabled(search) && !!inGodMode && isAdvisorScopedPath(pathname);
}

export default isAdvisorScopedPath;
